/**
 * ResearchProgressPanel
 *
 * Panel showing real-time progress of batch research.
 * Displays progress bar, stats, and completion summary.
 *
 * Part of Story 345: Enrichment Opportunities Dashboard
 *
 * Date: 2026-01-06
 */

import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import {
  Loader2,
  CheckCircle2,
  XCircle,
  RefreshCw,
  Clock,
  Search,
} from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import {
  getOpportunityDetails,
  triggerRegeneration,
  ResearchProgress,
  formatCost,
} from '@/services/enrichmentOpportunityService';

interface ResearchProgressPanelProps {
  opportunityId: string;
  attributeName: string;
  productCount: number;
  onComplete: () => void;
}

export function ResearchProgressPanel({
  opportunityId,
  attributeName,
  productCount,
  onComplete,
}: ResearchProgressPanelProps) {
  const { toast } = useToast();
  const [progress, setProgress] = useState<ResearchProgress | null>(null);
  const [isCompleted, setIsCompleted] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [regenerationResult, setRegenerationResult] = useState<{ productCount: number } | null>(null);

  // Poll for progress
  const pollProgress = useCallback(async () => {
    try {
      const details = await getOpportunityDetails(opportunityId);

      if (details.status === 'completed') {
        setProgress({
          processed: details.productsResearched || 0,
          total: productCount,
          valuesFound: details.valuesFound || 0,
          notAvailable: details.notAvailableCount || 0,
          failed: details.failedCount || 0,
          elapsedMs: 0,
          estimatedRemainingMs: 0,
          successRate: details.productsResearched
            ? Math.round((details.valuesFound || 0) / details.productsResearched * 100)
            : 0,
          completed: true,
        });
        setIsCompleted(true);
        return true;
      }

      // Still in progress - estimate from product status
      const completed = details.products.filter(p =>
        ['completed', 'failed', 'na'].includes(p.researchStatus)
      ).length;
      const inProgress = details.products.filter(p => p.researchStatus === 'in_progress').length;
      const valuesFound = details.products.filter(p => p.researchStatus === 'completed' && p.valueFound).length;
      const notAvailable = details.products.filter(p => p.researchStatus === 'na').length;
      const failed = details.products.filter(p => p.researchStatus === 'failed').length;

      setProgress({
        processed: completed,
        total: productCount,
        valuesFound,
        notAvailable,
        failed,
        elapsedMs: 0,
        estimatedRemainingMs: 0,
        successRate: completed > 0 ? Math.round(valuesFound / completed * 100) : 0,
        completed: false,
      });

      return false;
    } catch (error) {
      console.error('Failed to poll progress:', error);
      return false;
    }
  }, [opportunityId, productCount]);

  useEffect(() => {
    // Initial poll
    pollProgress();

    // Set up polling interval
    const interval = setInterval(async () => {
      const done = await pollProgress();
      if (done) {
        clearInterval(interval);
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [pollProgress]);

  const handleTriggerRegeneration = async () => {
    setIsRegenerating(true);
    try {
      const result = await triggerRegeneration(opportunityId);
      setRegenerationResult(result);
      toast({
        title: 'Regeneration queued',
        description: `${result.productCount} products queued for AEO regeneration.`,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to trigger regeneration',
        variant: 'destructive',
      });
    } finally {
      setIsRegenerating(false);
    }
  };

  const progressPercent = progress ? Math.round((progress.processed / progress.total) * 100) : 0;

  return (
    <Card className="border-blue-500/50">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              {isCompleted ? (
                <CheckCircle2 className="h-5 w-5 text-green-500" />
              ) : (
                <Loader2 className="h-5 w-5 animate-spin text-blue-500" />
              )}
              {attributeName.replace(/_/g, ' ')}
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              {isCompleted ? 'Research completed' : 'Researching...'}
            </p>
          </div>
          <Badge variant={isCompleted ? 'default' : 'secondary'}>
            {isCompleted ? 'Complete' : 'In Progress'}
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Progress bar */}
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span>{progress?.processed || 0} / {progress?.total || productCount}</span>
            <span>{progressPercent}%</span>
          </div>
          <Progress value={progressPercent} className="h-2" />
        </div>

        {/* Stats */}
        {progress && (
          <div className="grid grid-cols-3 gap-2 text-sm">
            <div className="text-center p-2 bg-green-50 dark:bg-green-950 rounded">
              <div className="font-semibold text-green-600">{progress.valuesFound}</div>
              <div className="text-xs text-muted-foreground">Found</div>
            </div>
            <div className="text-center p-2 bg-muted/50 rounded">
              <div className="font-semibold">{progress.notAvailable}</div>
              <div className="text-xs text-muted-foreground">N/A</div>
            </div>
            <div className="text-center p-2 bg-red-50 dark:bg-red-950 rounded">
              <div className="font-semibold text-red-600">{progress.failed}</div>
              <div className="text-xs text-muted-foreground">Failed</div>
            </div>
          </div>
        )}

        {/* Success rate */}
        {progress && progress.processed > 0 && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Success rate</span>
            <span className="font-medium">{progress.successRate}%</span>
          </div>
        )}

        {/* Completed actions */}
        {isCompleted && (
          <div className="space-y-2 pt-2 border-t">
            {!regenerationResult ? (
              <Button
                className="w-full"
                onClick={handleTriggerRegeneration}
                disabled={isRegenerating || (progress?.valuesFound || 0) === 0}
              >
                {isRegenerating ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Queueing...
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Regenerate AEO ({progress?.valuesFound || 0} products)
                  </>
                )}
              </Button>
            ) : (
              <div className="text-center text-sm text-green-600">
                <CheckCircle2 className="h-4 w-4 inline mr-1" />
                {regenerationResult.productCount} products queued for regeneration
              </div>
            )}

            <Button
              variant="outline"
              className="w-full"
              onClick={onComplete}
            >
              Done
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
